import axios from 'axios';
import setAuthToken from '../authToken';
import { AUTH } from './types';
import { setMsgInfo } from './msgInfo';
import createAction from './createAction';
import { createTypes } from './createTypes';
import { getUserPreferences } from './userPreferences';
import API from './api';
import i18next from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { clearActivity, startSessionClock } from '../components/SessionTimeout/idleTimeout';

export function getResError(data) {
  let errors = [];
  const responseData = data?.response?.data;

  if (typeof responseData === 'string') {
    return [responseData];
  }

  if (!responseData || typeof responseData !== 'object') {
    return [data?.message || 'Unable to process request'];
  }

  if (responseData.space && responseData.space.name) {
    errors = [...errors, responseData.space.name[0]];
  }

  if (responseData.user && responseData.user.username) {
    errors = [...errors, ...responseData.user.username];
  }

  if (responseData.user && responseData.user.email) {
    errors = [...errors, ...responseData.user.email];
  }

  if (responseData.user && responseData.user.password) {
    errors = [...errors, 'password: ' + responseData.user.password[0]];
  }

  if (responseData.errors && responseData.errors.username) {
    errors = [...errors, ...responseData.errors.username];
  }

  if (responseData.errors && responseData.errors.email) {
    errors = [...errors, ...responseData.errors.email];
  }

  if (responseData.errors && responseData.errors.password) {
    errors = [...errors, 'password: ' + responseData.errors.password[0]];
  }

  if (responseData.errors && responseData.errors.error) {
    errors = [...errors, responseData.errors.error[0]];
  }

  if (responseData.errors && responseData.errors.organisation) {
    errors = [...errors, responseData.errors.organisation[0]];
  }

  if (errors.length) {
    return errors;
  }

  let err = Object.values(responseData);
  if (Array.isArray(err)) {
    return err;
  }
  return [err];
}

export function setAuthUser(data = {}) {
  // saving user's data in the store
  return createAction(createTypes(AUTH).success, { user: data });
}

const authLoading = (isLoading = false) => {
  return createAction(createTypes(AUTH).loading, isLoading);
};

export function removeAuthUser() {
  setAuthToken();
  clearActivity();
  localStorage.removeItem('user');
  localStorage.removeItem('userPreferences');
  localStorage.removeItem('form_id');
}

export function logout(navigate) {
  return (dispatch) => {
    // removing token to request headers
    removeAuthUser();
    dispatch(setAuthUser());
  };
}

export function getLoginOtp(payload) {
  return async (dispatch) => {
    try {
      dispatch(authLoading(true));

      await axios.post(API + '/login/otp/', payload);
    } catch (err) {
      dispatch(authLoading(false));
      const error = getResError(err);
      dispatch(setMsgInfo({ success: false, msg: error }));
    }
  };
}

export function resendOTP(payload) {
  return async (dispatch) => {
    try {
      dispatch(authLoading(true));
      await axios.post(API + '/otp/resend/', payload);
      dispatch(authLoading(false));
      // dispatch(
      //   setMsgInfo(
      //     { 
      //       success: true, 
      //       msg: ['OTP Resent please check your mail.'] 
      //     }
      //   ),
      // );
    } catch (err) {
      dispatch(authLoading(false));
      const error = getResError(err);
      dispatch(setMsgInfo({ success: false, msg: error }));
    }
  };
}

export function validateOTP(payload, navigate) {
  return async (dispatch) => {
    try {
      dispatch(authLoading(true));

      const {
        data: { user, token, refresh },
      } = await axios.post(API + '/otp/verify/', payload);

      // log user in normally
      setAuthToken(token);
      startSessionClock();

      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(user));
      // deleteCookie("i18next")
      // localStorage.removeItem('i18nextLng');

      dispatch(setAuthUser({ ...user, isAuth: true, data: true }));
      await getUserPreferences()(dispatch);
      navigate('/dashboard', { replace: true });
      localStorage.removeItem('paymentBannerShown');
      window.location.reload();
      dispatch(authLoading(false));
    } catch (err) {
      dispatch(authLoading(false));
      const error = getResError(err);
      dispatch(setMsgInfo({ success: false, msg: error }));
    }
  };
}

export function login(payload, navigate, redirectPath) {
  return async (dispatch) => {
    try {
      dispatch(authLoading(true));

      const {
        data: { user, token, refresh, otp_verification, otp_browser_dependent },
      } = await axios.post(API + '/login/', payload);

      if (otp_browser_dependent && !localStorage.getItem('browserId')) {
        localStorage.setItem('browserId', uuidv4());
      } else if (!otp_browser_dependent) {
        localStorage.removeItem('browserId');
      }

      if (otp_verification) {
        // if otp is required
        // implement otp authentication
        localStorage.setItem('otp_email', user.email);
        localStorage.setItem('user_id', user.user_id);
        localStorage.setItem('preferred_language', user.preferred_language);
        
        // Only store phone info if both phone_number and phone_country_code exist
        if (user.phone_number && user.phone_country_code) {
          localStorage.setItem('otp_phone_number', user.phone_number);
          localStorage.setItem('otp_phone_country_code', user.phone_country_code);
        } else {
          // Clear any existing phone data
          localStorage.removeItem('otp_phone_number');
          localStorage.removeItem('otp_phone_country_code');
        }
        
        dispatch(authLoading(false));
        navigate('/otp');
      } else {
        // if otp is not required
        // log user in normally
        setAuthToken(token);
        startSessionClock();

        localStorage.setItem('refreshToken', refresh);
        localStorage.setItem('user', JSON.stringify(user));
        // deleteCookie("i18next")
        // localStorage.removeItem('i18nextLng');

        dispatch(setAuthUser({ ...user, isAuth: true, data: true }));
        await getUserPreferences()(dispatch);
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/dashboard');
          // navigate('/dashboard/home', { replace: true });
        }
        localStorage.removeItem('paymentBannerShown');
        window.location.reload();
      }
    } catch (err) {
      dispatch(authLoading(false));
      const error = getResError(err);
      dispatch(setMsgInfo({ success: false, msg: error }));
    }
  };
}

export function adminLogin(payload, navigate, redirectPath) {
  return async (dispatch) => {
    try {
      dispatch(authLoading(true));

      const {
        data: { user, token, refresh, otp_browser_dependent },
      } = await axios.post(API + `/login/?admin=${true}`, payload);

      if (otp_browser_dependent && !localStorage.getItem('browserId')) {
        localStorage.setItem('browserId', uuidv4());
      } else if (!otp_browser_dependent) {
        localStorage.removeItem('browserId');
      }
      setAuthToken(token);
      startSessionClock();
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch(setAuthUser({ ...user, isAuth: true, data: true }));
      await getUserPreferences()(dispatch);

      localStorage.removeItem('paymentBannerShown');
      window.location.reload();
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        navigate('/admin/dashboard'); // move to the admin dashboard
      }
      window.location.reload();
    } catch (err) {
      dispatch(authLoading(false));
      const error = getResError(err);
      dispatch(setMsgInfo({ success: false, msg: error }));
    }
  };
}

const baseSignup = (path, payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(API + path, payload);
      dispatch(
        setMsgInfo({
          success: true,
          msg: [i18next.t('Registration successful')],
        }),
      );
      return data;
    } catch (err) {
      const error = getResError(err);
      dispatch(
        setMsgInfo({
          success: false,
          msg: error,
        }),
      );
      throw err;
    }
  };
};

export function createOrgSignup(payload) {
  return baseSignup('/signup/create/organisation/', payload);
}

export function createPersonalSignup(payload) {
  return baseSignup('/signup/create/personal/', payload);
}

export function joinOrgSignup(name, payload) {
  return baseSignup(`/signup/join/organisation/${name}/`, payload);
}

export function joinPersonalSignup(name, payload) {
  return baseSignup(`/signup/join/personal/${name}/`, payload);
}

export function passwordReset(data) {
  return (dispatch) => {
    return axios
      .post(API + '/password/reset/', data)
      .then((data) => {
        dispatch(
          setMsgInfo({
            success: true,
            msg: ['Check your email for password reset link'],
          }),
        );
      })
      .catch((err) => {
        const error = getResError(err);
        dispatch(setMsgInfo({ success: false, msg: error }));
        throw err;
      });
  };
}

export function verifyEmail(data) {
  const URI = decodeURI(API + '/signup/verify/' + data);
  return (dispatch) => {
    return axios
      .get(URI)
      .then((data) => {
        dispatch(
          setMsgInfo({
            success: true,
            msg: ['User verified!'],
          }),
        );
      })
      .catch((err) => {
        const error = getResError(err);
        dispatch(setMsgInfo({ success: false, msg: error }));
        throw err;
      });
  };
}

export function verifyChangePasswordLink(data) {
  return (dispatch) => {
    return axios
      .get(API + '/password/reset/verify/' + data)
      .then((data) => {
        dispatch(
          setMsgInfo({
            success: true,
            msg: ['User verified!'],
          }),
        );
      })
      .catch((err) => {
        const error = getResError(err);
        dispatch(setMsgInfo({ success: false, msg: error }));
        throw err;
      });
  };
}

export function verifyPasswordChange(data) {
  return (dispatch) => {
    return axios
      .post(API + '/password/reset/verified/', data)
      .then((data) => {
        dispatch(
          setMsgInfo({
            success: true,
            msg: ['Password changed successfully'],
          }),
        );
        setInterval(function () {
          window.location = '/login';
        }, 2500);
      })
      .catch((err) => {
        const error = getResError(err);
        dispatch(setMsgInfo({ success: false, msg: error }));
        throw err;
      });
  };
}

export function passwordChange(data) {
  return (dispatch) => {
    return axios
      .put(API + '/password/change/new/', data)
      .then((data) => {
        dispatch(
          setMsgInfo({
            success: true,
            msg: ['Password updated successfully'],
          }),
        );
      })
      .catch((err) => {
        const error = getResError(err);
        dispatch(setMsgInfo({ success: false, msg: error }));
        throw err;
      });
  };
}
